// Whether Neo's chat is open on the boat's home (TOM-520): closed on a first
// visit, then the way the owner left it, per boat, in this browser.

const OPEN_PREFIX = 'nm.homeChat.';

type OpenStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storage(): OpenStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** True when the owner left home's chat open on this boat; false on a first visit. */
export function readHomeChatOpen(boatId: string, store: OpenStorage | null = storage()): boolean {
  try {
    return store?.getItem(`${OPEN_PREFIX}${boatId}`) === 'open';
  } catch {
    return false;
  }
}

/** Remember home's chat open or closed on this boat. Storage that refuses is ignored. */
export function writeHomeChatOpen(
  boatId: string,
  open: boolean,
  store: OpenStorage | null = storage()
): void {
  try {
    store?.setItem(`${OPEN_PREFIX}${boatId}`, open ? 'open' : 'closed');
  } catch {
    // Private mode or a full store: the chat starts closed on the next visit.
  }
}

/**
 * The boat pages that show the sparkle besides home: Alarms, an alarm's own
 * page (not the new-alarm and regenerate flows) and every board's page.
 */
export function isSparkleRoute(pathname: string, boatTo: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  if (path === `${boatTo}/alarms`) return true;
  const alarm = path.startsWith(`${boatTo}/alarms/`) ? path.slice(boatTo.length + 8) : null;
  if (alarm !== null) {
    return alarm !== '' && !alarm.includes('/') && alarm !== 'new' && alarm !== 'regenerate';
  }
  const board = path.startsWith(`${boatTo}/boards/`) ? path.slice(boatTo.length + 8) : null;
  return board !== null && board !== '' && !board.includes('/');
}

// Where home's board was scrolled (TOM-920), per boat, for this tab: coming
// back to home finds it there. Session storage, so a new tab starts at the top.
const SCROLL_PREFIX = 'nm.homeScroll.';

function sessionStore(): OpenStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The board's scroll on the boat's home in this tab; 0 when none was kept. */
export function readHomeScroll(boatId: string, store: OpenStorage | null = sessionStore()): number {
  try {
    const n = Number(store?.getItem(`${SCROLL_PREFIX}${boatId}`) ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeHomeScroll(
  boatId: string,
  top: number,
  store: OpenStorage | null = sessionStore()
): void {
  try {
    store?.setItem(`${SCROLL_PREFIX}${boatId}`, String(Math.max(0, Math.round(top))));
  } catch {
    // Not kept: home opens at the top.
  }
}
